import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const EASE = [0.22, 1, 0.36, 1];

// Scroll-triggered fade + rise for any block
export const Reveal = ({ children, delay = 0, y = 28, className = "", once = true, as = "div" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-12% 0px -12% 0px" });
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease: EASE, delay }}
      className={className}
    >
      {children}
    </MotionTag>
  );
};

// Signature masked line-by-line reveal (on load)
export const MaskedText = ({ lines = [], className = "", delay = 0, stagger = 0.12 }) => {
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em]">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.05, ease: EASE, delay: delay + i * stagger }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

// Words reveal for scroll (per-word rise)
export const MaskedWords = ({ text = "", className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const words = text.split(" ");
  return (
    <span ref={ref} className={className}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom">
          <motion.span
            className="inline-block"
            initial={{ y: "110%" }}
            animate={inView ? { y: "0%" } : {}}
            transition={{ duration: 0.8, ease: EASE, delay: delay + i * 0.04 }}
          >
            {w}&nbsp;
          </motion.span>
        </span>
      ))}
    </span>
  );
};

// Slow editorial marquee
export const Marquee = ({ items = [], className = "" }) => {
  const doubled = [...items, ...items];
  return (
    <div className={`relative w-full overflow-hidden ${className}`} aria-hidden="true">
      <div className="flex w-max animate-marquee will-change-transform">
        {doubled.map((item, i) => (
          <span key={i} className="flex items-center">
            <span className="font-serif italic text-cove-olive/80 text-4xl md:text-6xl px-8 md:px-14">
              {item}
            </span>
            <span className="h-1.5 w-1.5 rounded-full bg-cove-rose/70" />
          </span>
        ))}
      </div>
    </div>
  );
};

// Hover "pop", gentle lift + scale for cards / tiles
export const Pop = ({ children, className = "", style, lift = -8, testId }) => (
  <motion.div
    whileHover={{ y: lift, scale: 1.015 }}
    transition={{ type: "spring", stiffness: 260, damping: 20 }}
    className={className}
    style={style}
    data-testid={testId}
  >
    {children}
  </motion.div>
);

// Soft floating organic blobs, drifting background colour
const BLOB_PRESETS = {
  sage: "bg-cove-sage/40",
  blush: "bg-cove-blush/60",
  rose: "bg-cove-rose/30",
  blue: "bg-cove-blue/40",
  olive: "bg-cove-olive/20",
};

export const FloatingBlob = ({
  color = "sage",
  size = 380,
  className = "",
  delay = 0,
  drift = 40,
}) => (
  <motion.div
    aria-hidden="true"
    initial={{ opacity: 0 }}
    animate={{
      opacity: 1,
      y: [0, -drift, 0],
      x: [0, drift / 2, 0],
      scale: [1, 1.08, 1],
    }}
    transition={{
      opacity: { duration: 1.4, delay },
      y: { duration: 14, repeat: Infinity, ease: "easeInOut", delay },
      x: { duration: 18, repeat: Infinity, ease: "easeInOut", delay },
      scale: { duration: 16, repeat: Infinity, ease: "easeInOut", delay },
    }}
    style={{ width: size, height: size }}
    className={`pointer-events-none absolute rounded-full blur-[70px] ${BLOB_PRESETS[color]} ${className}`}
  />
);
